import { createContext, useContext } from "react";

/**
 * What the selected account can do, for features whose behaviour depends on
 * it. The default describes a directly connected wallet; the layer that knows
 * better about the acting account (e.g. one that acts through a relayer)
 * provides its own values.
 */
export type AccountCapabilities = Readonly<{
  /** Can sign Cosmos authz grants, which automations acting for the account
   * (such as auto-claim) need. */
  authzGrants: boolean;
  /** Share of a limit that moves with prices (such as a borrow limit) to
   * leave unused, in basis points. Non-zero for an account whose
   * transactions execute some seconds after their review, so that a price
   * move in between cannot push them over the limit. */
  priceLimitHeadroomBps: bigint;
}>;

export const CONNECTED_WALLET_CAPABILITIES: AccountCapabilities = {
  authzGrants: true,
  priceLimitHeadroomBps: 0n,
};

const Context = createContext<AccountCapabilities>(
  CONNECTED_WALLET_CAPABILITIES
);

export const AccountCapabilitiesProvider = Context.Provider;

export const useAccountCapabilities = (): AccountCapabilities =>
  useContext(Context);

/** The part of a limit that moves with prices which the selected account can
 * safely use (see `priceLimitHeadroomBps`). */
export const useUsableLimit = (limit: bigint): bigint => {
  const { priceLimitHeadroomBps } = useAccountCapabilities();
  return priceLimitHeadroomBps > 0n
    ? (limit * (10_000n - priceLimitHeadroomBps)) / 10_000n
    : limit;
};
